/**
 * What needs looking at, wherever you happen to be.
 *
 * The same argument SendingBanner makes for the circuit breaker — "this cannot
 * be a detail on one screen" — applied to the four signals it was never applied
 * to. An unverified domain lived only on Domains, a disabled webhook endpoint
 * only on Webhooks, a stalled form only on Forms, a bounce rate only on
 * Analytics; knowing whether anything needed attention meant touring four
 * screens, so nobody knew until something had been broken for a while.
 *
 * SILENT WHEN THERE IS NOTHING TO SAY, which is the normal case and the whole
 * point. A strip that always shows something is one people stop reading, and
 * then it is worth less than nothing — it occupies the space where a real
 * warning would have been noticed.
 *
 * A QUIET STRIP, chosen on 21 September from design/preview/
 * explore-log-details.html. One neutral line, 34px tall, where only the glyph
 * carries the item's colour; the rest open in a popover over the page rather
 * than pushing it down. The tinted strip it replaces had a wash per severity,
 * and a wash across the top of every screen reads as an alarm whatever it
 * says. The glyph says how bad; the words say what.
 *
 * SEVERITY BELONGS TO AN ITEM, which the first version of this strip got wrong
 * the moment it opened: it painted a whole list in the first item's colour, so
 * "Finish setting up sending" sat in danger red. Every row here carries its own
 * glyph in its own colour, and nothing else is coloured at all.
 *
 * Critical and warning share the triangle and differ by colour, the same call
 * the nav badges make, and the server sorts critical first so rank survives
 * without colour. `setup` gets a gear because it has to: accent and danger are
 * 2 greyscale levels apart, so a gear against a triangle is the only thing
 * separating "finish setting up" from "this is broken" for a reader who cannot
 * tell blue from red.
 *
 * A WARNING OR A SETUP ITEM CAN BE HIDDEN; A CRITICAL ONE NEVER CAN. Hidden is
 * remembered for the person and the organization, and lasts until the item
 * changes: an item is its title and detail, which carry its counts, so "2
 * endpoints are off" becoming 3 brings it back. The sidebar badges still count
 * a hidden item, so hiding it here never makes the problem invisible.
 */

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { Icon } from "../components/Sprites";
import { useDismiss } from "../hooks";
import { useSession } from "../session";
import type { AttentionItem } from "../api/types";

function Glyph({ item }: { item: AttentionItem }) {
  return (
    <span className="attention__glyph" data-severity={item.severity} aria-hidden="true">
      <Icon name={item.severity === "setup" ? "settings" : "warning"} size={14} />
    </span>
  );
}

const hideable = (item: AttentionItem) => item.severity !== "critical";

/** What an item says. When it changes, a hidden item comes back. */
const signature = (item: AttentionItem) => `${item.title}\n${item.detail}`;

/**
 * Which items this person has hidden in this organization, by kind, with what
 * each said when it was hidden.
 *
 * In this browser only. Storage can be missing or throw, in a private window or
 * with site data blocked; hiding then lasts until the page reloads, which fails
 * towards showing the item.
 */
function useHidden(userId: string | undefined, orgId: string | null | undefined) {
  const key = userId && orgId ? `opencomms.attention-hidden.${userId}.${orgId}` : null;

  const read = (): Record<string, string> => {
    if (!key) return {};
    try {
      const parsed: unknown = JSON.parse(window.localStorage.getItem(key) ?? "{}");
      return parsed && typeof parsed === "object" ? (parsed as Record<string, string>) : {};
    } catch {
      return {};
    }
  };

  const [hidden, setHidden] = useState<Record<string, string>>(read);

  // Switching organization changes the key without remounting the shell, so
  // the stored set is read again whenever the key does.
  useEffect(() => setHidden(read()), [key]);

  const hide = (item: AttentionItem) => {
    const next = { ...hidden, [item.kind]: signature(item) };
    setHidden(next);
    if (!key) return;
    try {
      window.localStorage.setItem(key, JSON.stringify(next));
    } catch {
      /* kept for this page only, as above */
    }
  };

  const isHidden = (item: AttentionItem) => hideable(item) && hidden[item.kind] === signature(item);

  return { hide, isHidden };
}

export function AttentionStrip({ items }: { items: AttentionItem[] }) {
  const { session, orgId } = useSession();
  const { hide, isHidden } = useHidden(session?.user.id, orgId);
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  useDismiss(open, container, () => setOpen(false));

  const visible = items.filter((item) => !isHidden(item));
  const [first, ...rest] = visible;

  // Hiding the popover's last item removes the popover, but not the wish to
  // see it: left open, it would spring open by itself the next time an item
  // arrived, over whatever the reader was doing.
  const nothingMore = rest.length === 0;
  useEffect(() => {
    if (nothingMore) setOpen(false);
  }, [nothingMore]);

  if (!first) return null;

  return (
    <div className="attention" role="status" ref={container}>
      <Glyph item={first} />
      <span className="attention__line">
        {/* The title is the link and the detail beside it is not. Making the
            whole row clickable would mean the sentence explaining the problem
            is also the control, so reading it and acting on it stop being
            separable. */}
        <Link to={first.href} className="attention__title">
          {first.title}
        </Link>
        <span className="attention__detail"> — {first.detail}</span>
      </span>

      {hideable(first) && (
        <button type="button" className="attention__hide" onClick={() => hide(first)}>
          Hide
        </button>
      )}

      {rest.length > 0 && (
        <button
          type="button"
          className="attention__more"
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={`${rest.length} more needing attention`}
          onClick={() => setOpen((v) => !v)}
        >
          +{rest.length}
          <Icon name="chevron" size={12} className={open ? "chevron-up" : "chevron-down"} />
        </button>
      )}

      {open && rest.length > 0 && (
        <div className="oc-menu attention__pop" role="dialog" aria-label="More that needs attention">
          {rest.map((item) => (
            <div className="attention__item" key={item.kind}>
              <Glyph item={item} />
              <span className="attention__text">
                <Link to={item.href} className="attention__title" onClick={() => setOpen(false)}>
                  {item.title}
                </Link>
                <span className="attention__detail">{item.detail}</span>
              </span>
              {hideable(item) && (
                <button type="button" className="attention__hide" onClick={() => hide(item)}>
                  Hide
                </button>
              )}
            </div>
          ))}
          <div className="attention__foot">
            A hidden item comes back if it changes. The sidebar badges still count it.
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * How many outstanding items point at a given screen.
 *
 * Counts items rather than summing their `count`: the nav badge answers "is
 * there something here" and a 3 that means "three dead-lettered deliveries"
 * beside a 1 that means "one disabled endpoint" invites arithmetic across two
 * different units. Hidden items still count, deliberately.
 */
export function attentionFor(items: AttentionItem[], href: string): number {
  return items.filter((item) => item.href === href).length;
}

/** The worst severity pointing at a screen, for colouring its badge. */
export function severityFor(items: AttentionItem[], href: string): string | null {
  const here = items.filter((item) => item.href === href);
  if (here.length === 0) return null;
  if (here.some((item) => item.severity === "critical")) return "critical";
  if (here.some((item) => item.severity === "warning")) return "warning";
  return "setup";
}
